<template>
	<!-- main -->
	<div class="w-full md:w-3/4 lg:w-4/5 h-full flex flex-col items-start pair-bg overflow-y-auto custom-scrollbar md:rounded-tr-2xl md:rounded-bl-none md:rounded-tl-none rounded-b-2xl z-10">
		<div class="flex items-center justify-between w-full px-4 md:pt-14 md:px-8">
			<div class="flex items-center justify-center ml-4 mt-2 md:ml-0">
				<img :src="(currentTheme === 'dark' || currentTheme === 'navy') ? darkHistory : lightHistory" alt="Wallet Icon" class="w-6 h-6 md:w-8 md:h-8 mr-3"/>
				<h1 class="text-2xl md:text-4xl font-archivomedium text-[var(--text-color)]">
					Transaction History
				</h1>
				<!--<img :src="selectedChain.icon" alt="Chain Icon" class="w-10 h-10 mb-4 ml-2 mt-3 cursor-pointer md:top-16 md:right-16"/>-->
				<button @click="$emit('backToOverview')" class="hidden md:block absolute top-8 right-8">
					<img :src="(currentTheme === 'dark' || currentTheme === 'navy') ? darkX : lightX" alt="Close Icon" class="w-3 h-3"/>
				</button>
			</div>
			<!-- back button -->
			<button @click="$emit('backToOverview')" class="block md:hidden ml-auto mt-2 mr-4 flex items-center space-x-1 px-3 py-2 text-sm font-archivoregular text-[var(--secondary-text-color)] bg-[var(--accent-color-50)] hover:bg-[var(--accent-color-30)] rounded-full transition-colors">
				<svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
					<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"/>
				</svg>
				<span class="hover:text-[var(--secondary-text-color)] text-xs">Back</span>
			</button>
		</div>

		<!-- mobile-only chain-pill -->
		<div class="flex justify-start w-full md:hidden mt-4 px-8">
			<span class="inline-flex items-center space-x-1 bg-[var(--accent-color-10)] text-[var(--accent-color)] px-3 py-2 rounded-full">
				<img :src="selectedChain.icon" alt="Chain Icon" class="w-5 h-5 flex-shrink-0"/>
				<span class="text-sm font-archivoregular">
					{{ selectedChain.name }}
				</span>
			</span>
		</div>

		<!-- transaction history table -->
		<div class="w-full px-4 md:px-8 py-6">
			<div v-if="!transactions.length" class="text-center text-[var(--secondary-text-color)] my-6">
				No tx data available. Please try again later. 
			</div>

			<div v-else>
				<div v-if="selectedChain.name === 'Bitcoin'" class="overflow-x-auto">
					<table class="min-w-full divide-y divide-[var(--border-color)]">
						<thead class="bg-[var(--dropdown-bg-color)] rounded-t-lg overflow-hidden">
							<tr>
								<th class="first:rounded-tl-lg px-4 py-2 w-24 text-left text-xs font-archivosemibold text-[var(--secondary-text-color)] uppercase">Date</th>
								<th class="px-4 py-2 text-left text-xs font-archivosemibold text-[var(--secondary-text-color)] uppercase">Type</th>
								<th class="px-4 py-2 text-left text-xs font-archivosemibold text-[var(--secondary-text-color)] uppercase">Block</th>
								<th class="px-4 py-2 text-left text-xs font-archivosemibold text-[var(--secondary-text-color)] uppercase">Inputs</th>
								<th class="px-4 py-2 text-left text-xs font-archivosemibold text-[var(--secondary-text-color)] uppercase">Outputs</th>
								<th class="px-4 py-2 text-left text-xs font-archivosemibold text-[var(--secondary-text-color)] uppercase">Amount</th>
								<th class="px-4 py-2 text-left text-xs font-archivosemibold text-[var(--secondary-text-color)] uppercase">Status</th>
								<th class="px-4 py-2 w-24 text-left text-xs font-archivosemibold text-[var(--secondary-text-color)] uppercase">Confirmations</th>
								<th class="last:rounded-tr-lg px-4 py-2 text-left text-xs font-archivosemibold text-[var(--secondary-text-color)] uppercase">View Tx</th>
							</tr>
						</thead>
						<tbody class="bg-[var(--tx-history-bg-color)] divide-y divide-[var(--border-color)]">
							<tr v-for="tx in transactions" :key="tx.id" :class="{ 'bg-[var(--accent-color-10)]': tx.status === 'Pending' }">
								<td class="px-4 py-4 text-xs text-[var(--btc-tx-date-colour)]">
									{{ tx.date }}
								</td>
								<td class="px-4 py-3 text-xs">
									<span class="inline-flex whitespace-nowrap text-center" :class="{'bg-[var(--new-bg-received)] px-2 py-1 rounded-full text-[var(--new-text-receive)]': tx.type === 'Receive' || tx.type === 'Received', 'bg-[var(--new-bg-sent)] px-3 py-1 rounded-full text-[var(--new-text-sent)]' : tx.type === 'Send' || tx.type === 'Sent'}">
										{{ tx.type }}
									</span>
								</td>
								<td class="px-4 py-4 text-xs text-[var(--btc-tx-block-colour)]">
									{{ tx.block }}
								</td>
								<!-- inputs -->
								<td class="px-4 py-4 align-middle">
									<div v-for="(inp,i) in tx.inputsArr" :key="i" class="relative group my-2">
										<span class="truncate text-xs text-[var(--btc-tx-inputs-colour)] cursor-pointer block w-full">
											{{ inp.slice(0,6) + '...' + inp.slice(-6) }}
										</span>
										<div class="absolute left-0 bottom-full mb-1 hidden group-hover:block bg-[var(--dropdown-bg-2-color)] text-[var(--text-color)] text-xs px-3 py-2 rounded shadow-lg whitespace-nowrap z-10">
											{{ inp }}
										</div>
									</div>
								</td>
								<!-- outputs -->
								<td class="px-4 py-4 align-middle">
									<div v-for="(out,j) in tx.outputsArr" :key="j" class="relative group my-2">
										<span class="truncate text-xs text-[var(--btc-tx-outputs-colour)] cursor-pointer block w-full">
											{{ out.slice(0,6) + '...' + out.slice(-6) }}
										</span>
										<div class="absolute left-0 bottom-full mb-1 hidden group-hover:block bg-[var(--dropdown-bg-2-color)] text-[var(--text-color)] text-xs px-3 py-2 rounded shadow-lg whitespace-nowrap z-10">
											{{ out }}
										</div>
									</div>
								</td>
								<!-- amounts -->
								<td class="px-4 py-4 align-middle">
									<div v-for="(val,j) in tx.outputValuesArr" :key="j" class="text-xs text-[var(--btc-tx-amount-colour)] mb-1">
										{{ val }}
									</div>
								</td>
								<td class="w-16 px-4 py-3 align-middle text-center">
									<div class="flex justify-center">
										<!-- Confirmed -->
										<svg v-if="tx.status === 'Confirmed'" width="20" height="20" viewBox="0 0 16 16" class="drop-shadow-sm">
											<circle cx="8" cy="8" r="8" fill="#9FFFCA" />
											<path d="M4.5 8.5l2 2 4-4" stroke="#00671D" stroke-width="2" fill="none" stroke-linecap="round" stroke-linejoin="round"/>
										</svg>
										<!-- Pending -->
										<svg v-else-if="tx.status === 'Pending'" width="20" height="20" viewBox="0 0 20 20" class="drop-shadow-sm">
											<circle cx="10" cy="10" r="10" fill="#FFD5AE" />
											<path d="M10 6v5" stroke="#EC5900" stroke-width="2.5" stroke-linecap="round"/>
											<circle cx="10" cy="14" r="1.5" fill="#EC5900" />
										</svg>
										<!-- Rejected -->
										<svg v-else width="20" height="20" viewBox="0 0 20 20" class="drop-shadow-sm">
											<circle cx="10" cy="10" r="10" fill="#FDB9B9" />
											<path d="M6 6l8 8M14 6l-8 8" stroke="#D40606" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
										</svg>
									</div>
								</td>
								<td class="px-4 py-4 text-xs text-[var(--btc-tx-confirmations-colour)]">
									{{ tx.confirmations }}
								</td>
								<td class="px-4 py-4 text-xs">
									<button @click="openModal(tx)" class="p-1">
										<img :src="(currentTheme === 'dark' || currentTheme === 'navy') ? darkExpandView : lightExpandView" alt="View Details" class="w-5 h-5"/>
									</button>
								</td>
							</tr>
						</tbody>
					</table>
				</div>

				<div v-else class="overflow-x-auto">
					<table class="min-w-full divide-y divide-[var(--border-color)]">
						<thead class="bg-[var(--dropdown-bg-color)] rounded-t-lg overflow-hidden">
							<tr class="rounded-t-lg">
								<th class="first:rounded-tl-lg px-4 py-2 text-left text-xs font-archivosemibold text-[var(--secondary-text-color)] uppercase">Block</th>
								<th class="px-4 py-2 text-left text-xs font-archivosemibold text-[var(--secondary-text-color)] uppercase">Type</th>
								<th class="px-4 py-2 text-left text-xs font-archivosemibold text-[var(--secondary-text-color)] uppercase">Amount</th>
								<th class="px-4 py-2 text-left text-xs font-archivosemibold text-[var(--secondary-text-color)] uppercase">From</th>
								<th class="px-4 py-2 text-left text-xs font-archivosemibold text-[var(--secondary-text-color)] uppercase">Interacted With (To)</th>
								<!--<th class="px-4 py-2 text-left text-xs font-archivosemibold text-[var(--secondary-text-color)] uppercase">Confirmations</th>-->
								<th class="px-4 py-2 text-left text-xs font-archivosemibold text-[var(--secondary-text-color)] uppercase">Hash</th>
								<th class="last:rounded-tr-lg px-4 py-2 text-left text-xs font-archivosemibold text-[var(--secondary-text-color)] uppercase">View Tx</th>
							</tr>
						</thead>
						<tbody class="bg-[var(--tx-history-bg-color)] divide-y divide-[var(--border-color)]">
							<tr v-for="tx in transactions" :key="tx.id">

								<td class="px-4 py-3 text-xs text-[var(--evm-tx-block-colour)]">
									{{ tx.block }}
								</td>

								<td class="px-4 py-3 text-xs">
									<span  class="inline-flex whitespace-nowrap text-center" :class="{ 
										'text-[var(--text-color)]': !(tx.amount.split(' ')[0] === '0' || tx.type === 'Sent' || tx.type === 'Received'),
										'bg-[var(--new-bg-received)] px-2 py-1 rounded-full text-[var(--new-text-receive)]': tx.type === 'Received', 
										'bg-[var(--new-bg-sent)] px-3 py-1 rounded-full text-[var(--new-text-sent)]': tx.type === 'Sent', 
										'bg-[var(--new-bg-swap)] px-2 py-1 rounded-full text-[var(--new-text-swap)]': tx.amount.split(' ')[0] === '0'}">
										{{ tx.amount.split(' ')[0] === '0' ? 'Smart Contract' : tx.type }}
									</span>
								</td>

								<td class="px-4 py-3 text-xs text-[var(--evm-tx-amount-colour)]">
									{{ tx.amount.split(' ')[0] === '0' ? '-' : tx.amount }}
								</td>

								<td class="px-4 py-3 align-middle">
									<div class="relative group">
										<span  @click="emit('copy-address', tx.from)" class="truncate text-xs text-[var(--evm-tx-from-colour)] cursor-pointer block w-full">
											{{ tx.from.slice(0,6) + '...' + tx.from.slice(-6) }}
										</span>
										<div class="absolute left-0 bottom-full mb-1 hidden group-hover:block bg-[var(--dropdown-bg-2-color)] text-[var(--text-color)] text-xs px-3 py-2 rounded shadow-lg whitespace-nowrap z-10"> 
											{{ tx.from }}
										</div>
									</div>
								</td>

								<td class="px-4 py-3 align-middle">
									<div class="relative group">
										<span @click="emit('copy-address', tx.to)" class="truncate text-xs text-[var(--evm-tx-to-colour)] cursor-pointer block w-full">
											{{ tx.to.slice(0,6) + '...' + tx.to.slice(-6) }}
										</span>
										<div class="absolute left-0 bottom-full mb-1 hidden group-hover:block bg-[var(--dropdown-bg-2-color)] text-[var(--text-color)] text-xs px-3 py-2 rounded shadow-lg whitespace-nowrap z-10">
											{{ tx.to }}
										</div>
									</div>
								</td>

								<!--<td class="px-4 py-3 text-xs text-[var(--evm-tx-confirmations-colour)]">
									{{ tx.confirmations }}
								</td>-->

								<td class="px-4 py-3 text-xs">
									<div class="relative group">
										<button @click="$emit('copy-hash', tx.hash)" class="text-xs text-[var(--evm-tx-hash-colour)] truncate block" :title="tx.hash">
											{{ tx.hash.slice(0,6) + '...' + tx.hash.slice(-4) }}
										</button>
										<div class="absolute right-0 bottom-full mb-1 hidden group-hover:block bg-[var(--dropdown-bg-2-color)] text-[var(--text-color)] text-xs px-3 py-2 rounded shadow-lg whitespace-nowrap z-10">
											{{ tx.hash }}
										</div>
									</div>
								</td>
								<!-- <td class="px-4 py-2 text-sm text-[var(--text-color)]">{{ tx.viewMore }}</td>-->
								<td class="px-4 py-3">
									<button @click="openModal(tx)" class="p-1">
										<img :src="(currentTheme === 'dark' || currentTheme === 'navy') ? darkExpandView : lightExpandView" alt="View Details" class="w-5 h-5"/>
									</button>
								</td>
							</tr>
						</tbody>
					</table>
				</div>
			</div>
		</div>

	<TransactionDetailsModal
		:show="showModal"
		:tx="selectedTx"
		:explorer-url="explorerUrl"
		:current-theme="currentTheme"
		:selectedChain="selectedChain"
		@close="showModal = false"
	/>

	</div>
</template>

<script setup>
	import { ref } from 'vue';
	import lightX from '../assets/X.png';
	import darkX  from '../assets/XWhite.png';
	import lightHistory from '../assets/txHistoryIconLightTheme.png';
	import darkHistory from '../assets/txHistoryIconDarkTheme.png';
	import lightExpandView from '../assets/ExpandViewLightTheme.png';
	import darkExpandView from '../assets/ExpandViewDarkTheme.png';

	import TransactionDetailsModal from './transactionDetailsPopup.vue';

	const props = defineProps({
		currentTheme: { type: String, required: true },
		selectedChain: { type: Object, required: true },
		transactions: { type: Array, required: true },
		explorerUrl: { type: String, required: true },
	});

	const emit = defineEmits(['backToOverview', 'copy-hash', 'copy-address']);

	const showModal = ref(false);
	const selectedTx = ref({});

	function openModal(tx) {
		selectedTx.value = {
			block: tx.block,
			from: tx.from || '—',
			to: tx.to || '—',
			amount: tx.amount,
			fees: tx.fees || '—',
			date: tx.date,
			confirmations: tx.confirmations ?? '—',
			type: tx.type,
			status: tx.status,
			hash: tx.hash || '—',
			inputsArr: tx.inputsArr || [],
			outputsArr: tx.outputsArr || [],
			outputValuesArr: tx.outputValuesArr || []
		};
		showModal.value = true;
	}

	async function copyAddress(address) {
		try {
			await navigator.clipboard.writeText(address);
			showSuccessToast('Address copied to clipboard');
		} catch {
			showErrorToast('Failed to copy address');
		}
	}
</script>

<style scoped>

</style>